/**
 * What the merchant reads when a chat turn ends in an error — one place, for every agent.
 *
 * The raw error is written for us, not for them. It is the proxy's notice (chatbot-api
 * `DigitalStaffWsProxyHandler`), a runtime error the proxy passed through, or a string this
 * client made up when a send failed. Most of it is plumbing ("Digital Staff WebSocket closed
 * (4004)", "proxy setup failed: …"), and the turn-stalled notice ends in "Please send it
 * again". So Maya's page hid every error behind one fixed banner — "Maya had trouble
 * connecting. Please send your message again." — and Oscar's and Anna's printed the raw text.
 *
 * Both were wrong in the same way (10-26, kiechee): the turn had not lost its connection, and
 * sending again is the one thing not to do after a turn that already saved part of its work —
 * a resent "write 5 drafts" that had saved 3 left 12 (test 2026-10-23, YEPAI-12 / D3).
 *
 * One source for the words: the server's. chatbot-api writes its notices for the merchant
 * (#2412 — the agent's name, how many drafts were kept, "the batch is carrying on"), and only
 * it knows which of those is true. So a notice that reads as merchant copy is shown exactly as
 * sent. This file only speaks when the server's text cannot be shown: there is none, it still
 * says the old things ("trouble connecting", "send it again"), or it is plumbing (internal
 * names, error codes, a runtime's raw error) — and for the errors this client raises itself.
 * Rewriting a good notice is not safe: "Your last message came in while Maya was writing this
 * batch … The batch is carrying on" would come out as "Maya couldn't finish this reply".
 */

/** Codes this client stamps on errors it raises itself (never sent by the server). */
export const CLIENT_ERROR_CODES = {
  /** The message never went out — the socket could not be (re)opened for the send. */
  SEND_FAILED: 'CLIENT_SEND_FAILED',
  /** Every automatic reconnect attempt failed while a reply was still in flight. */
  CONNECTION_LOST: 'CLIENT_CONNECTION_LOST',
} as const;

/** The two codes the proxy stamps on a turn it gave up on (see FRAME_CODE_* there). */
const INTERRUPTED_CODES = new Set(['TURN_STALLED', 'UPSTREAM_RESTARTED']);
/** The runtime refused a message sent while a turn was running (ZeroClaw steering). */
const STEERING_CODES = new Set(['STEERING_CLOSED', 'STEERING_QUEUE_FULL']);
/** Codes whose message is the runtime's own text, passed through — never merchant copy. */
const RUNTIME_TEXT_CODES = new Set([
  ...STEERING_CODES,
  'AUTH_ERROR',
  'PROVIDER_ERROR',
  'AGENT_ERROR',
  'SESSION_QUEUE_FULL',
  'SESSION_QUEUE_TIMEOUT',
]);

/** What the server used to say and must not reach the merchant any more. */
const RETIRED_WORDING = /trouble connecting|send (?:it|your message) again/i;
/** Plumbing: internal names, raw errors, status / error codes. */
const PLUMBING =
  /digital staff|websocket|proxy|upstream|zeroclaw|\bmcp\b|steering|exception|stack trace|\(\d{3,4}\)|\b[A-Z][A-Z0-9]*_[A-Z0-9_]+\b/i;
/** The protocol's stand-in for a frame that carried no message at all (protocol.ts). */
const NO_MESSAGE = 'Agent error';

/** The server's own text is fit to show as it is. */
function isMerchantCopy(message: string): boolean {
  if (!message || message === NO_MESSAGE) return false;
  if (RETIRED_WORDING.test(message) || PLUMBING.test(message)) return false;
  // Merchant copy is a sentence; "send failed", "timed out" and the like are log lines.
  return /^[A-Z“"']/.test(message);
}

/** `interruptedNotice(draftsSaved)` in the proxy: "… 3 drafts were already saved — …". */
const DRAFTS_SAVED = /(\d+)\s+drafts?\s+(?:was|were)\s+already\s+saved/i;

export interface TurnError {
  message: string | null | undefined;
  code?: string | null;
}

function draftsSavedIn(message: string): number {
  const match = DRAFTS_SAVED.exec(message);
  const n = match ? Number(match[1]) : 0;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function interrupted(agentName: string, draftsSaved: number): string {
  if (draftsSaved > 0) {
    const drafts = draftsSaved === 1 ? '1 draft was' : `${draftsSaved} drafts were`;
    return (
      `${agentName} couldn't finish this reply, but ${drafts} already saved. ` +
      `Check your drafts first, then ask ${agentName} for the rest — that way nothing gets written twice.`
    );
  }
  return `${agentName} couldn't finish this reply. Your chat is saved — just ask ${agentName} to pick up where you left off.`;
}

/**
 * The merchant-facing sentence for a turn error. `agentName` is the agent's first name
 * (`CHAT_AGENT_CONFIGS[agentType].name`) — the same copy serves Maya, Oscar and Anna.
 */
export function describeTurnError(error: TurnError, agentName: string): string {
  const message = (error.message ?? '').trim();
  const code = (error.code ?? '').trim().toUpperCase();

  const ours =
    code === CLIENT_ERROR_CODES.SEND_FAILED || code === CLIENT_ERROR_CODES.CONNECTION_LOST;
  if (!ours && !RUNTIME_TEXT_CODES.has(code) && isMerchantCopy(message)) {
    return message;
  }

  // Fallbacks — the server's text could not be shown as it is.
  if (code === CLIENT_ERROR_CODES.SEND_FAILED) {
    // Not "check your connection": this is also what an agent that could not be started looks
    // like from here (its quota full, its runtime not coming up), and the network is fine then.
    return `Your last message didn't go through to ${agentName}. Please try it again in a moment.`;
  }
  if (code === CLIENT_ERROR_CODES.CONNECTION_LOST) {
    return `Lost the connection to ${agentName}. Your chat is saved — it reconnects automatically the next time you send a message.`;
  }
  if (STEERING_CODES.has(code)) {
    return `${agentName} was already wrapping up and didn't take in your last message. Once this reply is finished, ask again.`;
  }
  if (INTERRUPTED_CODES.has(code)) {
    return interrupted(agentName, draftsSavedIn(message));
  }
  // Uncoded frames the proxy still sends: the session-limit rejection, and its own
  // "still connecting" back-pressure. Everything else uncoded is a turn that ended early.
  if (/too many active/i.test(message)) {
    return 'Too many chats are open at once. Close another tab or chat, then try again.';
  }
  if (/still connecting/i.test(message)) {
    return `${agentName} is still getting ready. Give it a moment, then try again.`;
  }
  return interrupted(agentName, draftsSavedIn(message));
}
